import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Save, Send, Loader2, ShieldCheck, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { SkeletonCards } from '../Brand/BrandLoading';
import { ukDate } from '../../lib/format';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMING_HELP = {
  booking_confirmation: 'Sent by hand from any job, including jobs still in Holding.',
  balance_reminder: 'Days before the balance due date. Only when a due date and an amount are set, and the balance is not ticked as paid.',
  cocktail_reminder: 'Days before the event, for cocktail jobs with no choices in yet.',
  meet_your_host: 'The day before the event, from this hour. Only once someone is assigned.',
  review_request: 'The day after the job is marked complete, from this hour.',
};

export const EmailSettings = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sweeping, setSweeping] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/emails`, { headers });
      setData(res.data);
      setTestEmail((current) => current || res.data.test_email_default || '');
    } catch (err) {
      toast.error('Could not load the email settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const settings = data?.settings;

  const patch = (updates) => setData({ ...data, settings: { ...settings, ...updates } });
  const patchType = (key, updates) => patch({
    types: { ...settings.types, [key]: { ...settings.types[key], ...updates } },
  });

  const toggle = async (key, enabled) => {
    try {
      const res = await axios.post(`${API}/admin/settings/emails/toggle`,
        { email_type: key, enabled }, { headers });
      setData({ ...data, settings: res.data.settings });
      toast.success(enabled
        ? 'Switched on. It only applies to events from today onwards.'
        : 'Switched off. Nothing more will send automatically.');
    } catch (err) {
      toast.error('Could not change that switch');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings/emails`, {
        daily_cap: Number(settings.daily_cap) || 30,
        review_link: settings.review_link,
        send_from_hour: Number(settings.send_from_hour),
        send_to_hour: Number(settings.send_to_hour),
        types: Object.fromEntries(Object.entries(settings.types).map(([key, value]) => [key, {
          subject: value.subject || '',
          intro: value.intro || '',
          ...(value.days_before !== undefined ? { days_before: Number(value.days_before) } : {}),
          ...(value.hour !== undefined ? { hour: Number(value.hour) } : {}),
        }])),
      }, { headers });
      setData({ ...data, settings: res.data.settings });
      toast.success('Email settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save those settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (key) => {
    setTesting(key);
    try {
      const res = await axios.post(`${API}/admin/emails/test`,
        { email_type: key, to_email: testEmail || undefined }, { headers });
      if (res.data.status === 'sent') toast.success(`Test sent to ${res.data.to}`);
      else if (res.data.status === 'logged') toast.message('No mailbox password here, so the test was written to the log instead');
      else toast.error('The test could not be sent');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send that test');
    } finally {
      setTesting('');
    }
  };

  const runSweep = async () => {
    setSweeping(true);
    try {
      const res = await axios.post(`${API}/admin/emails/run-sweep`, {}, { headers });
      const sent = res.data.sent?.length || 0;
      toast.success(sent
        ? `${sent} email${sent === 1 ? '' : 's'} sent`
        : `Nothing was due (${res.data.reason || 'all checks passed'})`);
      load();
    } catch (err) {
      toast.error('Could not run the check');
    } finally {
      setSweeping(false);
    }
  };

  if (loading) return <SkeletonCards count={2} testId="email-settings-loading" />;
  if (!settings) return null;

  return (
    <Card className="bd-card-accent bg-white max-w-4xl" data-testid="email-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="w-5 h-5 text-gold-deep" /> Customer emails
        </CardTitle>
        <p className="mt-2 text-sm text-slate-500">
          Everything here starts switched off. When you switch one on, it only ever applies to
          events from that day onwards — nothing historic is ever emailed.
        </p>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="flex flex-wrap items-center gap-3 rounded-[10px] border border-hair bg-cream p-3 text-sm">
          <ShieldCheck className="h-4 w-4 shrink-0 text-gold-deep" />
          <span className="text-ink" data-testid="email-smtp-status">
            {data.smtp_ready ? 'The mailbox is connected, so emails send for real.'
              : 'No mailbox password here, so emails are written to the log instead of sending.'}
          </span>
          <span className="text-slate-500" data-testid="email-sent-today">
            {data.sent_today} automatic email{data.sent_today === 1 ? '' : 's'} today
          </span>
        </div>

        {data.types.map((type) => {
          const config = settings.types[type.key];
          return (
            <div key={type.key} className="rounded-[12px] border border-hair p-4"
                 data-testid={`email-type-${type.key}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="bd-heading text-sm text-ink">{type.label}</h3>
                  <p className="mt-1 max-w-xl text-xs text-slate-500">{TIMING_HELP[type.key]}</p>
                  {config.enabled && config.enabled_at && (
                    <p className="mt-1 text-xs text-gold-deep" data-testid={`email-live-from-${type.key}`}>
                      Live for events from {ukDate(config.enabled_at, { withYear: true })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold uppercase tracking-wide ${config.enabled ? 'text-green-700' : 'text-slate-400'}`}>
                    {config.enabled ? 'On' : 'Off'}
                  </span>
                  <Switch
                    checked={!!config.enabled}
                    onCheckedChange={(value) => toggle(type.key, value)}
                    aria-label={`Switch ${type.label} ${config.enabled ? 'off' : 'on'}`}
                    data-testid={`email-switch-${type.key}`}
                  />
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {config.days_before !== undefined && (
                  <div>
                    <label className="text-xs font-semibold text-slate-500">Days before</label>
                    <Input type="number" min="0" max="60" value={config.days_before}
                           onChange={(e) => patchType(type.key, { days_before: e.target.value })}
                           data-testid={`email-days-${type.key}`} />
                  </div>
                )}
                {config.hour !== undefined && (
                  <div>
                    <label className="text-xs font-semibold text-slate-500">Send from (hour, UK)</label>
                    <Input type="number" min="9" max="19" value={config.hour}
                           onChange={(e) => patchType(type.key, { hour: e.target.value })}
                           data-testid={`email-hour-${type.key}`} />
                  </div>
                )}
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-500">
                    Subject (leave blank for the standard one)
                  </label>
                  <Input value={config.subject || ''} placeholder="Uses {service}, {date}, {ref}, {first_name}, {host}"
                         onChange={(e) => patchType(type.key, { subject: e.target.value })}
                         data-testid={`email-subject-${type.key}`} />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-500">
                    Opening line (leave blank for the standard one)
                  </label>
                  <Input value={config.intro || ''}
                         onChange={(e) => patchType(type.key, { intro: e.target.value })}
                         data-testid={`email-intro-${type.key}`} />
                </div>
              </div>

              <Button variant="outline" size="sm" className="mt-3"
                      disabled={testing === type.key}
                      onClick={() => sendTest(type.key)}
                      data-testid={`email-test-${type.key}`}>
                {testing === type.key
                  ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  : <Send className="mr-1 h-4 w-4" />}
                Send a test to me
              </Button>
            </div>
          );
        })}

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Daily safety cap</label>
            <Input type="number" min="1" max="500" value={settings.daily_cap}
                   onChange={(e) => patch({ daily_cap: e.target.value })}
                   data-testid="email-daily-cap" />
            <p className="mt-1 text-xs text-slate-500">If it's hit, we pause and tell you.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Send between (from)</label>
            <Input type="number" min="0" max="23" value={settings.send_from_hour}
                   onChange={(e) => patch({ send_from_hour: e.target.value })}
                   data-testid="email-from-hour" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Send between (to)</label>
            <Input type="number" min="1" max="24" value={settings.send_to_hour}
                   onChange={(e) => patch({ send_to_hour: e.target.value })}
                   data-testid="email-to-hour" />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-500">Send tests to</label>
          <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                 placeholder="you@butlerdirect.co.uk" data-testid="email-test-address" />
          <p className="mt-1 text-xs text-slate-500">
            Where "Send a test to me" goes. Tests use a sample booking, or the job you were last looking at.
          </p>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-500">Review link</label>
          <Input value={settings.review_link || ''}
                 onChange={(e) => patch({ review_link: e.target.value })}
                 data-testid="email-review-link" />
          <p className="mt-1 text-xs text-slate-500">
            Used by the review request. Swap in a Google review link whenever you like.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving} data-testid="email-settings-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save email settings
          </Button>
          <Button variant="outline" onClick={runSweep} disabled={sweeping}
                  data-testid="email-run-sweep">
            {sweeping ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
            Run the check now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailSettings;
